class InspirationSwitcher extends HTMLElement {
  constructor() {
    super();
    this.items = this.querySelectorAll('.inspiration-switcher__item');
    this.primaryImageContainer = this.querySelector('.inspiration-switcher__image-primary');
    this.secondaryImageContainer = this.querySelector('.inspiration-switcher__image-secondary');
    this.ctaElement = this.querySelector('.inspiration-switcher__cta');
    this.mediaColumn = this.querySelector('.inspiration-switcher__media-column');
    this.activeIndicator = this.querySelector('.inspiration-switcher__indicator-active');
    this.isTransitioning = false;
  }

  connectedCallback() {
    this.items.forEach((item) => {
      item.addEventListener('click', this.handleItemClick.bind(this));
      item.addEventListener('keydown', this.handleKeydown.bind(this));
    });

    // Initialize indicator position
    this.updateIndicatorPosition(this.querySelector('.inspiration-switcher__item.is-active'));
  }

  handleItemClick(/** @type {Event} */ event) {
    const item = /** @type {HTMLElement} */ (event.currentTarget);
    if (item.classList.contains('is-active') || this.isTransitioning) return;
    this.switchToItem(item);
  }

  handleKeydown(event) {
    const items = Array.from(this.items);
    const currentIndex = items.indexOf(event.currentTarget);
    let newIndex;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex + 1 >= items.length ? 0 : currentIndex + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex - 1 < 0 ? items.length - 1 : currentIndex - 1;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = items.length - 1;
        break;
      default:
        return;
    }

    const targetItem = /** @type {HTMLElement} */ (items[newIndex]);
    if (targetItem) {
      targetItem.focus();
      this.switchToItem(targetItem);
    }
  }

  switchToItem(item) {
    this.isTransitioning = true;

    // Update active states for tabs
    this.items.forEach((i) => {
      i.classList.remove('is-active');
      i.setAttribute('aria-selected', 'false');
      i.setAttribute('tabindex', '-1');
    });

    item.classList.add('is-active');
    item.setAttribute('aria-selected', 'true');
    item.setAttribute('tabindex', '0');

    // Update panel aria-labelledby
    const sectionId = this.dataset.sectionId;
    const blockId = item.dataset.blockId;
    if (this.mediaColumn) {
      this.mediaColumn.id = `Panel-${sectionId}-${blockId}`;
      this.mediaColumn.setAttribute('aria-labelledby', `Tab-${sectionId}-${blockId}`);
    }

    // Update indicator position
    this.updateIndicatorPosition(item);

    // Crossfade images
    this.crossfadeImages(item);

    // Update CTA
    this.updateCta(item);
  }

  updateIndicatorPosition(item) {
    if (!this.activeIndicator || !item) return;

    const itemRect = item.getBoundingClientRect();
    const itemsContainer = this.querySelector('.inspiration-switcher__items');
    const containerRect = itemsContainer.getBoundingClientRect();

    const offsetTop = itemRect.top - containerRect.top;
    const height = itemRect.height;

    this.activeIndicator.style.transform = `translateY(${offsetTop}px)`;
    this.activeIndicator.style.height = `${height}px`;
  }

  crossfadeImages(item) {
    const image1Url = item.dataset['image-1'] || '';
    const image1Alt = item.dataset['image-1Alt'] || '';
    const image1Width = item.dataset['image-1Width'] || '';
    const image1Height = item.dataset['image-1Height'] || '';
    const image2Url = item.dataset['image-2'] || '';
    const image2Alt = item.dataset['image-2Alt'] || '';
    const image2Width = item.dataset['image-2Width'] || '';
    const image2Height = item.dataset['image-2Height'] || '';

    const primaryImg = this.primaryImageContainer.querySelector('img, svg');
    const secondaryImg = this.secondaryImageContainer?.querySelector('img, svg');

    // Add fading class
    if (primaryImg) primaryImg.classList.add('is-fading');
    if (secondaryImg) secondaryImg.classList.add('is-fading');

    // After fade out, swap images
    setTimeout(() => {
      // Store the CTA element before replacing innerHTML
      const ctaElement = this.primaryImageContainer.querySelector('.inspiration-switcher__cta');

      if (image1Url && image1Url !== '') {
        this.primaryImageContainer.innerHTML = `<img
          src="${image1Url}"
          alt="${image1Alt}"
          width="${image1Width || 'auto'}"
          height="${image1Height || 'auto'}"
          class="inspiration-switcher__img is-fading"
          loading="lazy"
        >`;
      } else {
        this.primaryImageContainer.innerHTML = this.getPlaceholderSvg();
      }

      // Re-append the CTA element if it exists
      if (ctaElement) {
        this.primaryImageContainer.appendChild(ctaElement);
      }

      if (this.secondaryImageContainer) {
        if (image2Url && image2Url !== '') {
          this.secondaryImageContainer.innerHTML = `<img
            src="${image2Url}"
            alt="${image2Alt}"
            width="${image2Width || 'auto'}"
            height="${image2Height || 'auto'}"
            class="inspiration-switcher__img is-fading"
            loading="lazy"
          >`;
        } else {
          this.secondaryImageContainer.innerHTML = this.getPlaceholderSvg();
        }
      }

      // Fade in primary image first
      requestAnimationFrame(() => {
        const newPrimaryImg = this.primaryImageContainer?.querySelector('img, svg');
        if (newPrimaryImg) newPrimaryImg.classList.remove('is-fading');
      });

      // Stagger secondary image by 150ms
      setTimeout(() => {
        requestAnimationFrame(() => {
          const newSecondaryImg = this.secondaryImageContainer?.querySelector('img, svg');
          if (newSecondaryImg) newSecondaryImg.classList.remove('is-fading');
          this.isTransitioning = false;
        });
      }, 50);
    }, 200);
  }

  updateCta(item) {
    const linkUrl = item.dataset.linkUrl;
    const linkLabel = item.dataset.linkLabel;

    if (!this.ctaElement) return;

    if (linkUrl && linkLabel) {
      // Create anchor element if it doesn't exist or is a span
      if (this.ctaElement.tagName === 'SPAN') {
        const newCta = document.createElement('a');
        newCta.href = linkUrl;
        newCta.className = this.ctaElement.className;
        newCta.textContent = linkLabel;
        newCta.classList.remove('is-hidden');
        this.ctaElement.replaceWith(newCta);
        this.ctaElement = newCta;
      } else {
        this.ctaElement.href = linkUrl;
        this.ctaElement.textContent = linkLabel;
        this.ctaElement.classList.remove('is-hidden');
        this.ctaElement.removeAttribute('aria-disabled');
      }
    } else if (linkLabel) {
      // Show label without link
      if (this.ctaElement.tagName === 'A') {
        const newCta = document.createElement('span');
        newCta.className = this.ctaElement.className;
        newCta.textContent = linkLabel;
        newCta.setAttribute('aria-disabled', 'true');
        this.ctaElement.replaceWith(newCta);
        this.ctaElement = newCta;
      } else {
        this.ctaElement.textContent = linkLabel;
        this.ctaElement.classList.remove('is-hidden');
      }
    } else {
      this.ctaElement.classList.add('is-hidden');
    }
  }

  getPlaceholderSvg() {
    return `<svg class="inspiration-switcher__img inspiration-switcher__placeholder" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525 525" fill="none">
      <path fill="currentColor" fill-opacity=".1" d="M0 0h525v525H0z"/>
    </svg>`;
  }
}

if (!customElements.get('inspiration-switcher')) {
  customElements.define('inspiration-switcher', InspirationSwitcher);
}
